import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import ChatGateway from "../../api/gateways/ChatGateway";
import { getChatAssistantAllowedUserIds } from "../../config/chatAssistantAllowlist";
import { UserRoles } from "../../entities/IUser";
import { screens } from "../../screens/Navigation.types";
import { useCounterService } from "../../services/useCounterService";
import { useAppDispatch } from "../../store";
import { authActions } from "../../store/auth/AuthReducer";
import { useGetFiltersByPage } from "../../store/filters/FilterSelectors";
import { MenuItem } from "./SideNav.types";

function isStringInMenuArray(menuArray: MenuItem[], searchString: string) {
  for (const item of menuArray) {
    if (item.path === searchString) {
      return true;
    }

    if (item.children) {
      if (isStringInMenuArray(item.children, searchString)) {
        return true;
      }
    }
  }

  return false;
}

function useGetTasksFilters() {
  const filters = useGetFiltersByPage(screens.tasks);
  // IF task filters are not defined add default timeframe and return query string
  if (!filters) {
    const fQuery = new URLSearchParams();
    fQuery.append(
      "filters",
      JSON.stringify({
        name: "createdAt",
        type: "dateArray",
        operator: "between",
        value: JSON.stringify([
          "2025-01-01T00:00:00.000Z",
          new Date().toISOString(),
        ]),
      }),
    );
    return fQuery.toString();
  }
  // If filters are defined check if the time frame is added, if not add default timeframe
  const fQuery = new URLSearchParams(filters);
  const fData = fQuery.getAll("filters");
  if (!fData.some((item) => item.includes("createdAt"))) {
    fQuery.append(
      "filters",
      JSON.stringify({
        name: "createdAt",
        type: "dateArray",
        operator: "between",
        value: JSON.stringify([
          "2025-01-01T00:00:00.000Z",
          new Date().toISOString(),
        ]),
      }),
    );
    return fQuery.toString();
  }
  return filters;
}

export function useSideNav() {
  const navigate = useNavigate();
  const [history, setHistory] = useState<string[]>([]);
  const [chatAccessRoles, setChatAccessRoles] = useState<UserRoles[]>([]);
  const location = useLocation();
  const [activeRoute, setActiveRoute] = useState<string>(
    location.pathname.split(screens.root)[1]
      ? location.pathname.split(screens.root)[1]
      : screens.documents.replace("/", ""),
  );
  const dispatch = useAppDispatch();
  const { allTasksCounter, myTasksCounter } = useCounterService();
  const myTasksFilters = useGetFiltersByPage(screens.myTasks);
  const allTasksFilters = useGetTasksFilters();
  const documentsFilters = useGetFiltersByPage(screens.documents);
  const archiveFilters = useGetFiltersByPage(screens.archive);
  const archiveStatsFilters = useGetFiltersByPage(screens.archiveStats);
  const taskStatsFilters = useGetFiltersByPage(screens.taskStats);
  const reportsFilters = useGetFiltersByPage(screens.reports);
  const digitalSignatureFilters = useGetFiltersByPage(screens.digitalSignature);
  const chatAssistantAllowedUserIds = useMemo(
    () => getChatAssistantAllowedUserIds(),
    [],
  );

  // useEffect(() => {
  //   const getChatAccessRoles = async () => {
  //     try {
  //       const roles = await ChatGateway.api.getChatAccessRoles();
  //       setChatAccessRoles(roles);
  //     } catch (error) {
  //       console.error("Error fetching chat access roles => ", error);
  //     }
  //   };
  //   getChatAccessRoles();
  // }, []);

  const logOut = () => {
    dispatch(authActions.setUser(null));
    navigate(screens.login);
  };
  const generateSideMenuItems = (
    allTasksCounter: number,
    myTasksCounter: number,
    chatAccessRoles: UserRoles[],
    assistantAllowedUserIds: number[],
  ) => {
    return [
      {
        icon: "fa-regular fa-folder-open",
        label: "Documents",
        value: "documents",
        allowedRoles: [],
        children: [
          {
            icon: "",
            label: "Documents",
            value: "documents",
            path: screens.documents,
            allowedRoles: [],
            query: documentsFilters,
          },
          {
            icon: "",
            label: "Archive",
            value: "archive",
            path: screens.archive,
            allowedRoles: [],
            query: archiveFilters,
          },
          {
            icon: "",
            label: "Dashboard",
            value: "archive-stats",
            path: screens.archiveStats,
            allowedRoles: [UserRoles.ADMIN, UserRoles.SUPERVISOR],
            query: archiveStatsFilters,
          },
          {
            icon: "",
            label: "Signatures",
            value: "digital-signature",
            path: screens.digitalSignature,
            allowedRoles: [],
            query: digitalSignatureFilters,
            pilot: false,
          },
        ],
      },
      {
        icon: "fa-solid fa-tasks",
        label: "Tasks",
        value: "tasks",
        allowedRoles: [
          UserRoles.ADMIN,
          UserRoles.SUPERVISOR,
          UserRoles.BO_EXPERT,
        ],
        query: allTasksFilters,
        children: [
          {
            icon: "",
            label: "All Tasks",
            value: "tasks",
            path: screens.tasks,
            counter: allTasksCounter,
            allowedRoles: [
              UserRoles.ADMIN,
              UserRoles.SUPERVISOR,
              UserRoles.BO_EXPERT,
            ],
            query: allTasksFilters,
          },
          {
            icon: "",
            label: "My Tasks",
            value: "my-tasks",
            path: screens.myTasks,
            counter: myTasksCounter,
            allowedRoles: [
              UserRoles.ADMIN,
              UserRoles.SUPERVISOR,
              UserRoles.BO_EXPERT,
            ],
            query: myTasksFilters,
          },
          {
            icon: "",
            label: "Baskets",
            value: "baskets",
            path: screens.baskets,
            allowedRoles: [
              UserRoles.ADMIN,
              UserRoles.SUPERVISOR,
              UserRoles.BO_EXPERT,
            ],
          },
          {
            icon: "",
            label: "Dashboard",
            value: "task-stats",
            path: screens.taskStats,
            allowedRoles: [UserRoles.ADMIN, UserRoles.SUPERVISOR],
            query: taskStatsFilters,
          },
          {
            icon: "",
            label: "Reports",
            value: "reports",
            path: screens.reports,
            allowedRoles: [UserRoles.ADMIN, UserRoles.SUPERVISOR],
            query: reportsFilters,
          },
          /* {
            icon: "",
            label: "Rules",
            value: "baskets-rules",
            path: screens.basketRules,
          },*/
        ],
      },
      {
        icon: "fa-solid fa-comments",
        label: "AI Assistant",
        value: "chatbot",
        path: screens.chatbot,
        allowedRoles: chatAccessRoles,
        allowedUserIds:
          assistantAllowedUserIds.length > 0
            ? assistantAllowedUserIds
            : undefined,
      },
    ];
  };

  const sideMenuItems = useMemo(
    () =>
      generateSideMenuItems(
        allTasksCounter,
        myTasksCounter,
        chatAccessRoles,
        chatAssistantAllowedUserIds,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      allTasksCounter,
      myTasksCounter,
      myTasksFilters,
      allTasksFilters,
      documentsFilters,
      archiveFilters,
      archiveStatsFilters,
      taskStatsFilters,
      reportsFilters,
      chatAccessRoles,
      chatAssistantAllowedUserIds,
    ],
  );

  useEffect(() => {
    setHistory((prev) => [location.pathname.split(screens.root)[1], ...prev]);
    if (
      isStringInMenuArray(
        sideMenuItems,
        "/" + location.pathname.split(screens.root)[1],
      )
    ) {
      setActiveRoute(location.pathname.split(screens.root)[1]);
    }
  }, [location, sideMenuItems]);

  return {
    navigate,
    activeRoute,
    logOut,
    history,
    sideMenuItems,
  };
}
